import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { selectFetchingDefinition, selectSelectedReport } from 'app/area/favourite/selectors';
import { AddInstance, ClearInstance } from 'app/area/filters/action-types';
import { actionCreators } from 'app/area/filters/actions';
import { advancedField, createFiltersDefinition, mapToFilter } from 'app/area/filters/favourite-definition/map-to-filters';
import { createReportDefinition } from 'app/area/filters/report/map-to-filters';
import { FilterOptionType } from 'app/area/filters/types';
import { updateFilterConditions } from 'app/components/filters/filters-panel/conditions/update-filter-conditions';
import { customFilterFieldPostfix } from 'app/components/filters/filters-panel/constants';
import { mapConnections } from 'app/components/filters/filters-panel/map-to-connections';
import { Filter, FilterDomainNode, FilterDomains, FilterMenuOptions, FilterMenuOptionsTypes, ReportData } from 'app/components/filters/types';
import { STATE } from 'app/constants';
import { useSharedState } from 'app/infrastructure/redux/hooks';
import { useSimpleForm } from 'app/infrastructure/simple-form/hooks';
import { valuesSelector } from 'app/infrastructure/simple-form/selectors';
import { SimpleFormAreaState, SimpleFormParameters, SimpleFormValuesStoreData } from 'app/infrastructure/simple-form/types';
import { AdvancedSearchTypes, FavouriteCondition, FavouriteConnections, FavouriteDefinition } from 'app/types-shared';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useActionCreators as useFavouriteActionCreators } from 'app/area/favourite/hooks';
import { dateToUTC } from 'app/infrastructure/data/date';

export function useInstanceSlice() {
  const dispatch = useDispatch();
  const instanceKey = useInstanceKey();
  
  useEffect(
    () => {
      dispatch(new AddInstance(instanceKey));
      return () => {
        dispatch(new ClearInstance(instanceKey));
      };
    },
    [instanceKey]
  );
}

export function useInstanceStatePath() {
  const instanceKey = useInstanceKey();
  return useMemo(() => [STATE.filters, 'instances', instanceKey], [instanceKey]);
}

const factoryParams = { displayNotSelectedOption: true };

export function useInstanceSimpleForm(simpleFormParameters?: SimpleFormParameters) {
  const instanceStatePath = useInstanceStatePath();
  return useSimpleForm(instanceStatePath, factoryParams, simpleFormParameters);
}

export function useLoadFiltersAndAdvancedSearchTypesEffect(reportType?: number, reportId?: number) {
  const {
    loadFilters,
    loadAdvancedSearchTypes,
    clearAll
  } = useActionCreators();
  useEffect(() => {
    if(!reportId && !reportType) {
      return () => undefined;
    }
    
    loadFilters(reportType, reportId);
    loadAdvancedSearchTypes();
    return () => clearAll();
  }, [loadFilters, loadAdvancedSearchTypes, clearAll, reportType, reportId]);
}

export function useUpdateFiltersFromDataEffect(
  definition: Partial<FavouriteDefinition>, 
  filterDomains: FilterDomainNode[], 
  advancedSearchTypes: AdvancedSearchTypes[], 
  translation: any
) {
  const {
    updateFilters
  } = useActionCreators();
  
  useEffect(() => {
    if (!translation || !definition || !filterDomains?.length || !advancedSearchTypes?.length) {
      return;
    }
    
    updateFilters(
      createFiltersDefinition(
        definition, 
        filterDomains, 
        advancedSearchTypes, 
        translation.filters_predefined_filters
      )
    );
  }, [definition, filterDomains, advancedSearchTypes, translation]);
}

export function useUpdateConditionsAndConnectionsFromSimpleFormEffect(
  updateDefinition: (transform?: (definition: FavouriteDefinition) => FavouriteDefinition) => void,
  currentList: Filter[],
  editingValues?: SimpleFormValuesStoreData
) {
  useEffect(() => {
    if (!editingValues) {
      return;
    }

    const transform = (workingDefinition: FavouriteDefinition): FavouriteDefinition => {

      const conditions: FavouriteCondition[] = updateFilterConditions(currentList, editingValues, workingDefinition.conditions) || [];
      const predefinedFilter: Filter = currentList.find(item => item.domain === FilterDomains.PredefFilters);
      const connections: FavouriteConnections[] = predefinedFilter ? 
        mapConnections(currentList, editingValues, workingDefinition.connections) : workingDefinition.connections;

      return {
        ...workingDefinition,
        conditions,
        connections
      };
    };

    updateDefinition(transform);
  
  }, [editingValues, currentList]);
}

export function useUpdateFiltersFromCompanyReportDataEffect(
  definition: Partial<FavouriteDefinition>,
  filterDomains: FilterDomainNode[],
  advancedSearchTypes: AdvancedSearchTypes[],
  reportData: ReportData,
  predefinedTitle: string,
  translation: any
) {
  const {
    updateFilters
  } = useActionCreators();
  
  useEffect(() => {
    if ((!definition || !filterDomains?.length && !reportData) || !advancedSearchTypes?.length ) {
      return;
    }

  const predefDateFromDefinition = definition?.reports?.filter(r=>r.report.v === definition.selectedReport.v)[0].predefDate;
  const predefDate = !!predefDateFromDefinition && dateToUTC(new Date(predefDateFromDefinition));

    updateFilters(
      createReportDefinition(
        definition,
        filterDomains,
        advancedSearchTypes,
        predefDate,
        predefinedTitle,
        true
      )
    );
  }, [definition, filterDomains, advancedSearchTypes, reportData?.usePredefDate, translation]);
}

export function useSimpleFormToCompanyReportDefinitionEffect(
  editingValues: SimpleFormValuesStoreData,
  currentList: Filter[]
) {
  const instanceKey = useInstanceKey();
  const selectedReport = useSelector(selectSelectedReport(instanceKey))?.v;
  const fetchingDefinition = useSelector(selectFetchingDefinition(instanceKey));
  const editingValuesLength = Object.keys(editingValues || {}).length;

  const {
    transformWorkingDefinition
  } = useFavouriteActionCreators();

  useEffect(() => {
    if (fetchingDefinition || !currentList.length || !editingValuesLength) {
      return;
    }
    
    transformWorkingDefinition(definition => {
      const conditions = updateFilterConditions(currentList, editingValues, []) || [];

      const predefinedFilter = currentList.find(item => item.domain === FilterDomains.PredefDate);
      const predefinedFilterValues = editingValues?.[predefinedFilter.id + customFilterFieldPostfix.predefDate];
      const predefinedFilterValue = predefinedFilterValues?.value;

      return {
        ...definition,
        conditions,
        reports: definition.reports.map(r => r.report.v === selectedReport ? ({
          ...r,
          predefDate: predefinedFilterValue
        }) : r)
      };
    });
  }, [fetchingDefinition, editingValues, currentList, selectedReport, transformWorkingDefinition]);
}

export function useFilterEventHandlers(
  simpleForm: SimpleFormAreaState,
  currentList: Filter[],
  advancedSearchTypes: AdvancedSearchTypes[]
) {
  const {
    clearFilterOptions,
    clearValues,
    clearAllFilterOptions,
    setFilterOption,
    updateFilters
  } = useActionCreators();

  const clear = useCallback(() => {
    const ids = Object.keys(valuesSelector(simpleForm));

    clearAllFilterOptions();
    clearValues(ids);
  }, [simpleForm, clearAllFilterOptions, clearValues]);

  const onFilterMenuOption = useCallback((id: string, option: FilterMenuOptions, type: FilterMenuOptionsTypes) => {
    setFilterOption({ id, option, type }, FilterOptionType.MENU_OPTION);
  }, [setFilterOption]);

  const onRemoveFilters = useCallback((id: string) => {
    const ids = Object.keys(valuesSelector(simpleForm))
      .filter(element => element.indexOf(id) === 0);
    
    clearFilterOptions(id);
    clearValues(ids);
  }, [simpleForm, clearFilterOptions, clearValues]);

  const onAddFilters = useCallback((newFilters: Filter[]) => {
    updateFilters([
      ...currentList,
      ...newFilters.map(filter => ({
        ...mapToFilter(filter),
        advancedField: advancedField(advancedSearchTypes, filter.advancedSearchType)
      }))
    ]);
  }, [currentList, advancedSearchTypes]);

  return {
    clear,
    onFilterMenuOption,
    onRemoveFilters,
    onAddFilters
  };
}

export function useUserProfile() {
  const sharedState = useSharedState();
  
  return {
    id: sharedState.id,
    name: sharedState.name,
    unit: sharedState.unit,
    location: sharedState.location
  };
}

export function useActionCreators() {
  const dispatch = useDispatch();
  const instanceKey = useInstanceKey();

  return useMemo(
    () => bindActionCreators(actionCreators(instanceKey), dispatch), 
    [dispatch, instanceKey]
  );
}
